import manifest from "../assets/optimized/manifest.json"

type ManifestVariant = {
  format: string
  width: number
  height?: number
  path: string
  bytes?: number
  source_hash?: string
}

type ManifestEntry = {
  alias: string
  source: string
  variants: ManifestVariant[]
  consumers?: string[]
  base_widths?: number[]
  densities?: number[]
  formats?: string[]
}

const manifestSources: Record<string, ManifestEntry> = (manifest as any).sources || {}
const aliasIndex: Record<string, ManifestEntry> = Object.values(manifestSources).reduce(
  (acc, entry: ManifestEntry) => {
    acc[entry.alias] = entry
    return acc
  },
  {} as Record<string, ManifestEntry>,
)

const FALLBACK_FORMATS = ["png", "jpeg", "jpg", "webp"]

const normalizeKey = (value: string) => value.replace(/^\.?\/?/, "")

const findEntry = (keyOrAlias: string): ManifestEntry | undefined => {
  const normalized = normalizeKey(keyOrAlias)
  if (manifestSources[normalized]) {
    return manifestSources[normalized]
  }

  if (manifestSources[`src/${normalized}`]) {
    return manifestSources[`src/${normalized}`]
  }

  if (aliasIndex[normalized]) {
    return aliasIndex[normalized]
  }

  return Object.values(manifestSources).find(
    (entry) => entry.source === normalized || entry.source.endsWith(normalized),
  )
}

const pickVariant = (variants: ManifestVariant[], targetWidth: number) => {
  if (!variants.length) return undefined
  const sorted = [...variants].sort((a, b) => a.width - b.width)
  const match = sorted.find((variant) => variant.width >= targetWidth)
  return match || sorted[sorted.length - 1]
}

const mimeForFormat = (format: string) => {
  if (format === "jpg" || format === "jpeg") return "image/jpeg"
  if (format === "webp") return "image/webp"
  return "image/png"
}

export type OptimizedAsset = {
  fallback: string
  width?: number
  height?: number
  sources: Array<{ type?: string; media?: string; srcSet: string }>
}

export const getOptimizedAsset = (
  keyOrAlias: string,
  targetWidth: number,
  preferredFormats: string[] = ["webp", "png", "jpeg", "jpg"],
): OptimizedAsset | null => {
  const entry = findEntry(keyOrAlias)
  if (!entry) {
    return null
  }

  const availableFormats = new Set(entry.variants.map((variant) => variant.format))
  const formatOrder = preferredFormats.filter((format) => availableFormats.has(format))
  if (!formatOrder.length) {
    return null
  }

  const grouped = formatOrder
    .map((format) => ({
      format,
      variants: entry.variants
        .filter((variant) => variant.format === format)
        .sort((a, b) => a.width - b.width),
    }))
    .filter((item) => item.variants.length)

  if (!grouped.length) {
    return null
  }

  const fallbackFormats = [...FALLBACK_FORMATS, ...formatOrder]
  let fallbackVariant: ManifestVariant | undefined

  for (const format of fallbackFormats) {
    const targetGroup = grouped.find((group) => group.format === format)
    if (!targetGroup) continue
    fallbackVariant = pickVariant(targetGroup.variants, targetWidth)
    if (fallbackVariant) {
      break
    }
  }

  fallbackVariant = fallbackVariant || grouped[0].variants[grouped[0].variants.length - 1]

  const sources = grouped.map((group) => ({
    type: mimeForFormat(group.format),
    srcSet: group.variants.map((variant) => `${variant.path} ${variant.width}w`).join(", "),
  }))

  return {
    fallback: fallbackVariant.path,
    width: fallbackVariant.width,
    height: fallbackVariant.height,
    sources,
  }
}
